import { useCallback, useState } from 'react';
import { checkJsonChanged, hasContent, jsonStringify } from '../../utils/jsonFormat';

export type GatewayConfigLayer = 1 | 2 | 3 | 4;

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export interface OverrideLayerStatus {
  isDirty: boolean;
  hasContent: boolean;
  label: string;
  color: ChipColor;
}

export function overrideLayerStatus(localJson: string, savedJson: string): OverrideLayerStatus {
  const isDirty = checkJsonChanged(localJson, savedJson);
  const hasSavedContent = hasContent(savedJson);
  return {
    isDirty,
    hasContent: hasSavedContent,
    label: isDirty ? 'Modified' : hasSavedContent ? 'Active' : 'None saved',
    color: isDirty ? 'warning' : hasSavedContent ? 'success' : 'default',
  };
}

export function pushLayerStatus(localJson: string, savedJson: string): {
  isDirty: boolean;
  hasPushOverrides: boolean;
  label: string;
  color: ChipColor;
} {
  const isDirty = checkJsonChanged(localJson, savedJson);
  const hasPushOverrides = hasContent(localJson);
  return {
    isDirty,
    hasPushOverrides,
    label: isDirty ? 'Modified' : hasPushOverrides ? 'Entered' : 'None entered',
    color: isDirty || hasPushOverrides ? 'warning' : 'default',
  };
}

export function useGatewayConfigLayerEdits() {
  const [editingLayer, setEditingLayer] = useState<GatewayConfigLayer | null>(null);
  const [pushOverridesJson, setPushOverridesJson] = useState('{}');
  const [savedPushOverridesJson, setSavedPushOverridesJson] = useState('{}');
  const [tenantOverridesJson, setTenantOverridesJson] = useState('{}');
  const [savedTenantOverridesJson, setSavedTenantOverridesJson] = useState('{}');
  const [gatewayOverridesJson, setGatewayOverridesJson] = useState('{}');
  const [savedGatewayOverridesJson, setSavedGatewayOverridesJson] = useState('{}');

  const openLayerDialog = useCallback((layer: GatewayConfigLayer) => {
    if (layer === 4) {
      setSavedPushOverridesJson(pushOverridesJson);
    }
    setEditingLayer(layer);
  }, [pushOverridesJson]);

  const closeLayerDialog = useCallback(() => {
    setEditingLayer(null);
  }, []);

  const applyPushLayerOnly = useCallback(() => {
    setSavedPushOverridesJson(pushOverridesJson);
    setEditingLayer(null);
  }, [pushOverridesJson]);

  const markTenantSaved = useCallback(() => {
    setSavedTenantOverridesJson(tenantOverridesJson);
  }, [tenantOverridesJson]);

  const markGatewaySaved = useCallback(() => {
    setSavedGatewayOverridesJson(gatewayOverridesJson);
  }, [gatewayOverridesJson]);

  const syncTenantOverrides = useCallback((data: unknown | undefined) => {
    if (data === undefined) return;
    const json = jsonStringify(data);
    setTenantOverridesJson(json);
    setSavedTenantOverridesJson(json);
  }, []);

  const syncGatewayOverrides = useCallback((data: unknown | undefined) => {
    if (data === undefined) return;
    const json = jsonStringify(data);
    setGatewayOverridesJson(json);
    setSavedGatewayOverridesJson(json);
  }, []);

  const tenantStatus = overrideLayerStatus(tenantOverridesJson, savedTenantOverridesJson);
  const gatewayStatus = overrideLayerStatus(gatewayOverridesJson, savedGatewayOverridesJson);
  const pushStatus = pushLayerStatus(pushOverridesJson, savedPushOverridesJson);

  return {
    editingLayer,
    setEditingLayer,
    pushOverridesJson,
    setPushOverridesJson,
    savedPushOverridesJson,
    tenantOverridesJson,
    setTenantOverridesJson,
    savedTenantOverridesJson,
    gatewayOverridesJson,
    setGatewayOverridesJson,
    savedGatewayOverridesJson,
    openLayerDialog,
    closeLayerDialog,
    applyPushLayerOnly,
    markTenantSaved,
    markGatewaySaved,
    syncTenantOverrides,
    syncGatewayOverrides,
    tenantStatus,
    gatewayStatus,
    pushStatus,
  };
}
